import { CDN_URL, WORKER_URL, catalogJsonUrl } from '../config.js';
import { fetchPrivateCharacterById } from './privateCharactersApi.js';
import { createRetryableHttpError, isTransientHttpStatus, shouldRetryMethod, withRetry } from './error-handling.js';
import { filterOutfitsForSfw, filterScenariosForSfw } from './nsfwTags.js';
import { getDefaultStandingActionId } from './spriteFallbacks.js';

// Prevents duplicate requests during page refresh with request deduplication

class FetchCache {
    constructor() {
        this.cache = new Map();
        this.pendingRequests = new Map();
        this.cacheExpiry = new Map();

        // Short TTLs with ETag-based extension for optimal performance
        this.defaultTTL = {
            // High-frequency data - short TTL, rely on ETag for efficiency (2 min)
            'characters_rankings.json': 2 * 60 * 1000,
            'characters_rankings_modal.json': 2 * 60 * 1000,
            'character_details': 2 * 60 * 1000,
            'scenarios-': 2 * 60 * 1000,

            // Medium-frequency data - moderate TTL (10 min)
            'voices/': 10 * 60 * 1000,
            'loc/': 10 * 60 * 1000,
            'response_styles.json': 10 * 60 * 1000,
            'nsfw_keywords.json': 10 * 60 * 1000,
            'outfits-': 10 * 60 * 1000,

            // Low-frequency data - longer TTL since changes are rare (1 hour)
            'outfits/': 60 * 60 * 1000,
            'translations/': 60 * 60 * 1000,
            'static/': 24 * 60 * 60 * 1000,
            'assets/': 24 * 60 * 60 * 1000,

            // User lists: in-memory until refresh, or until invalidate()
            'favorites/characters': 24 * 60 * 60 * 1000,
            'private/characters': 24 * 60 * 60 * 1000,
            'private/scenarios': 24 * 60 * 60 * 1000,

            'default': 2 * 60 * 1000  // 2 minutes default
        };
    }

    _normalizeAuthHeader(headers) {
        if (!headers) return '';
        const auth = headers.Authorization ?? headers.authorization;
        if (!auth || typeof auth !== 'string') return '';
        return auth.trim();
    }

    _getCacheKey(url, options = {}) {
        const method = options.method || 'GET';
        const body = this._normalizeRequestBody(options.body);
        const auth = this._normalizeAuthHeader(options.headers);

        const normalizedUrl = this._normalizeUrl(url);

        return auth
            ? `${method}:${normalizedUrl}:${body}:auth:${auth}`
            : `${method}:${normalizedUrl}:${body}`;
    }

    _isCacheableMethod(method) {
        const normalized = (method || 'GET').toUpperCase();
        return normalized === 'GET' || normalized === 'HEAD';
    }

    _normalizeRequestBody(body) {
        if (body === undefined || body === null) return '';
        if (typeof body === 'string') return body;
        if (typeof Blob !== 'undefined' && body instanceof Blob) return body;
        if (typeof FormData !== 'undefined' && body instanceof FormData) return body;
        if (typeof ArrayBuffer !== 'undefined' && body instanceof ArrayBuffer) return body;
        return JSON.stringify(body);
    }

    _normalizeUrl(url) {
        try {
            // Handle relative URLs by resolving them against current location
            const resolvedUrl = url.startsWith('http') ? url : new URL(url, window.location.href).href;
            const urlObj = new URL(resolvedUrl);
            const params = new URLSearchParams(urlObj.search);

            const sortedParams = new URLSearchParams();
            Array.from(params.entries())
                .sort(([a], [b]) => a.localeCompare(b))
                .forEach(([key, value]) => {
                    const normalizedValue = (value === 'null' || value === 'undefined' || value === '') ? 'null' : value;
                    sortedParams.set(key, normalizedValue);
                });

            urlObj.search = sortedParams.toString();
            return urlObj.toString();
        } catch (error) {
            console.warn('FetchCache: URL normalization failed:', error);
            console.log(url);
            return url;
        }
    }

    _normalizeLanguage(language) {
        if (typeof language !== 'string') return 'en';
        const trimmed = language.trim();
        if (!trimmed) return 'en';
        return /^[A-Za-z0-9-]+$/.test(trimmed) ? trimmed : 'en';
    }

    _buildHangoutFallbackPaths(type, entityId, language = 'en') {
        const normalizedLanguage = this._normalizeLanguage(language);
        const folder = type === 'scenario' ? 'scenarios' : 'characters';
        const base = `${CDN_URL}/data/${folder}/hangout`;
        const candidates = [
            `${base}/${normalizedLanguage}/${entityId}.json`,
            `${base}/en/${entityId}.json`,
            `${base}/${entityId}.json`
        ];

        return [...new Set(candidates)];
    }

    _getTTL(url) {
        for (const [pattern, ttl] of Object.entries(this.defaultTTL)) {
            if (pattern !== 'default' && url.includes(pattern)) {
                return ttl;
            }
        }
        return this.defaultTTL.default;
    }

    _isValid(key) {
        const expiry = this.cacheExpiry.get(key);
        return expiry && Date.now() < expiry;
    }

    _store(key, response, ttl) {
        this.cache.set(key, response);
        this.cacheExpiry.set(key, Date.now() + ttl);
    }

    /**
     * @param {string} url
     * @param {{ method?: string, headers?: Record<string, string>, body?: *, skipCache?: boolean, allowUnsafeRetry?: boolean, cache?: RequestCache, credentials?: RequestCredentials }} [options]
     */
    async fetch(url, options = {}) {
        const skipCache = options.skipCache === true;
        const allowUnsafeRetry = options.allowUnsafeRetry === true;
        const requestOptions = { ...options };
        delete requestOptions.skipCache;
        delete requestOptions.allowUnsafeRetry;
        const key = this._getCacheKey(url, requestOptions);
        const isCacheable = this._isCacheableMethod(requestOptions.method) && !skipCache;

        // First check: Is cache still valid based on TTL? (cacheable methods only)
        if (isCacheable && this._isValid(key)) {
            console.log(`FetchCache: Cache hit for ${url}`);
            const cached = this.cache.get(key);
            return cached.data || cached; // Return data property if it exists, otherwise return raw cached value
        }

        // Cache expired - check if we can use ETag to potentially extend TTL
        const cachedData = isCacheable ? this.cache.get(key) : null;
        if (cachedData && (cachedData.etag || cachedData.lastModified)) {
            console.log(`FetchCache: Cache expired, checking freshness with conditional request for ${url}`);
        } else {
            console.log(`FetchCache: Making fresh request for ${url}`);
        }

        if (this.pendingRequests.has(key)) {
            console.log(`FetchCache: Deduplicating request for ${url}`);
            try {
                return await this.pendingRequests.get(key);
            } catch (error) {
                this.pendingRequests.delete(key);
                throw error;
            }
        }

        const requestPromise = this._makeRequest(url, requestOptions, key, skipCache, allowUnsafeRetry);
        this.pendingRequests.set(key, requestPromise);

        try {
            const response = await requestPromise;
            return response;
        } catch (error) {
            this.pendingRequests.delete(key);
            throw error;
        } finally {
            if (this.pendingRequests.has(key)) {
                this.pendingRequests.delete(key);
            }
        }
    }

    async _makeRequest(url, options, key, skipCache = false, allowUnsafeRetry = false) {
        const method = String(options.method || 'GET').toUpperCase();
        const isCacheable = this._isCacheableMethod(options.method) && !skipCache;
        const requestOptions = { ...options };
        if (options.body !== undefined && options.body !== null) {
            requestOptions.body = this._normalizeRequestBody(options.body);
        }
        const cachedData = isCacheable ? this.cache.get(key) : null;

        if (cachedData && cachedData.etag) {
            requestOptions.headers = {
                ...requestOptions.headers,
                'If-None-Match': cachedData.etag
            };
        }

        if (cachedData && cachedData.lastModified) {
            requestOptions.headers = {
                ...requestOptions.headers,
                'If-Modified-Since': cachedData.lastModified
            };
        }

        if (!requestOptions.cache) requestOptions.cache = 'no-cache';

        const retryConfig = {
            method,
            endpoint: String(url),
            allowUnsafeMethods: allowUnsafeRetry
        };

        try {
            return await withRetry(async () => {
                const response = await fetch(url, requestOptions);

                if (response.status === 304) {
                    console.log(`FetchCache: 304 Not Modified for ${url}, using cached data`);
                    const ttl = this._getTTL(url);
                    this.cacheExpiry.set(key, Date.now() + ttl);
                    return cachedData.data || cachedData;
                }

                if (!response.ok) {
                    if (isTransientHttpStatus(response.status) && shouldRetryMethod(method, allowUnsafeRetry)) {
                        throw createRetryableHttpError(response, { endpoint: url, method });
                    }
                    const text = await response.text();
                    let errorBody;
                    try {
                        errorBody = JSON.parse(text);
                    } catch {
                        errorBody = { error: text };
                    }
                    const err = /** @type {any} */ (new Error(errorBody.error || errorBody.error_message || text || `HTTP ${response.status}: ${response.statusText}`));
                    err.status = response.status;
                    err.body = errorBody;
                    throw err;
                }

                const etag = response.headers.get('ETag');
                const lastModified = response.headers.get('Last-Modified');
                const data = await response.json();

                if (isCacheable) {
                    const cacheEntry = {
                        data,
                        etag,
                        lastModified,
                        timestamp: Date.now()
                    };
                    this._store(key, cacheEntry, this._getTTL(url));
                }

                return data;
            }, retryConfig, { label: 'fetchCache', endpoint: String(url), method });
        } catch (error) {
            console.error(`FetchCache: Request failed for ${url}:`, error);

            if (isCacheable && this.cache.has(key)) {
                console.log(`FetchCache: Returning stale cache for ${url}`);
                const cached = this.cache.get(key);
                return cached.data || cached;
            }
            throw error;
        }
    }

    async fetchJSON(url, options = {}) {
        return this.fetch(url, options);
    }

    async fetchBlob(url, options = {}) {
        const requestOptions = { ...options };
        delete requestOptions.skipCache;
        delete requestOptions.allowUnsafeRetry;
        const method = String(requestOptions.method || 'GET').toUpperCase();
        const key = `BLOB:${this._getCacheKey(url, requestOptions)}`;

        if (this.pendingRequests.has(key)) {
            return this.pendingRequests.get(key);
        }

        const requestPromise = withRetry(async () => {
            const response = await fetch(url, requestOptions);
            if (!response.ok) {
                if (isTransientHttpStatus(response.status) && shouldRetryMethod(method, false)) {
                    throw createRetryableHttpError(response, { endpoint: url, method });
                }
                throw new Error(`HTTP ${response.status}: ${response.statusText}`);
            }
            return response.blob();
        }, { method, endpoint: String(url), allowUnsafeMethods: false }, {
            label: 'fetchCache.blob',
            endpoint: String(url),
            method
        });

        this.pendingRequests.set(key, requestPromise);
        try {
            return await requestPromise;
        } catch (error) {
            this.pendingRequests.delete(key);
            throw error;
        } finally {
            if (this.pendingRequests.get(key) === requestPromise) {
                this.pendingRequests.delete(key);
            }
        }
    }

    async fetchText(url) {
        const key = `GET:${this._normalizeUrl(url)}::text`;
        if (this._isValid(key)) {
            const cached = this.cache.get(key);
            return cached.data || cached;
        }
        if (this.pendingRequests.has(key)) {
            return this.pendingRequests.get(key);
        }

        const requestPromise = (async () => {
            const response = await fetch(url, { cache: 'no-cache' });
            if (!response.ok) {
                throw new Error(`HTTP ${response.status}: ${response.statusText}`);
            }
            const data = await response.text();
            this._store(key, { data, timestamp: Date.now() }, this._getTTL(url));
            return data;
        })();

        this.pendingRequests.set(key, requestPromise);
        try {
            return await requestPromise;
        } finally {
            this.pendingRequests.delete(key);
        }
    }

    async fetchCharacterRankings() {
        const url = catalogJsonUrl('data/characters/characters_rankings.json');
        return this.fetchJSON(url);
    }

    async fetchOutfits(language = 'en') {
        const url = catalogJsonUrl(`data/outfits/outfits-${language}.json`);
        const data = await this.fetchJSON(url);
        if (Array.isArray(data)) return filterOutfitsForSfw(data);
        if (Array.isArray(data?.outfits)) {
            return { ...data, outfits: filterOutfitsForSfw(data.outfits) };
        }
        return data;
    }

    /**
     * Build video URL for character sprite (same convention as worker).
     */
    static buildVideoUrl(characterId, outfitId, actionId) {
        return `${CDN_URL}/s/videos/${characterId}_${outfitId}_${actionId}_00001.webm`;
    }

    async fetchCharacterSprite(characterId, actionId, outfitId = 0, gender = 'female', isFullSize = true, isVideo = false) {
        const resolvedActionId = (actionId !== null && actionId !== undefined && actionId !== '')
            ? actionId
            : getDefaultStandingActionId(gender);

        if (!isFullSize) {
            const spriteUrl = `${CDN_URL}/s/small/${characterId}_${outfitId}_${resolvedActionId}.webp`;
            return { spriteUrl, isVideo: false };
        }

        if (isVideo) {
            const videoUrl = FetchCache.buildVideoUrl(characterId, outfitId, resolvedActionId);
            return { spriteUrl: videoUrl, isVideo: true };
        }
        const spriteUrl = `${CDN_URL}/s/sprites/${characterId}/${outfitId}/${resolvedActionId}.webp`;
        return { spriteUrl, isVideo: false };
    }

    async fetchCharacterDetails(characterId, isPrivate = null, language = 'en') {
        // If isPrivate is explicitly set, use that
        if (isPrivate === true) {
            return await this._fetchPrivateCharacterDetails(characterId);
        } else if (isPrivate === false) {
            return await this._fetchPublicCharacterDetails(characterId, language);
        }

        // If isPrivate is null/undefined, try to auto-detect
        // First try public (faster), then private if that fails
        try {
            const publicData = await this._fetchPublicCharacterDetails(characterId, language);
            if (publicData) {
                return publicData;
            }
        } catch (error) {
            console.log(`Public fetch failed for character ${characterId}, trying private...`);
        }

        // If public failed, try private
        return await this._fetchPrivateCharacterDetails(characterId);
    }

    async _fetchPublicCharacterDetails(characterId, language = 'en') {
        const urls = this._buildHangoutFallbackPaths('character', characterId, language);
        let lastError = null;

        for (const url of urls) {
            try {
                return await this.fetchJSON(url);
            } catch (error) {
                lastError = error;
            }
        }

        if (lastError) throw lastError;
        throw new Error(`Character ${characterId} not found`);
    }

    async fetchPublicScenarioDetails(scenarioId, language = 'en') {
        const urls = this._buildHangoutFallbackPaths('scenario', scenarioId, language);

        for (const url of urls) {
            try {
                return await this.fetchJSON(url);
            } catch {
                // CDN miss. Try the next path, then the database fallback.
            }
        }

        const normalizedLanguage = this._normalizeLanguage(language);
        const headers = {};
        const token = await window.tokenManager?.getValidAccessToken?.() ?? null;
        if (token) headers.Authorization = `Bearer ${token}`;
        return this.fetchJSON(
            `${WORKER_URL}/scenario/hangout?scenario_id=${encodeURIComponent(scenarioId)}&language=${encodeURIComponent(normalizedLanguage)}`,
            { headers, skipCache: true }
        );
    }

    async _fetchPrivateCharacterDetails(characterId) {
        try {
            const character = await fetchPrivateCharacterById(characterId);
            if (!character) {
                console.warn(`Private character ${characterId} not found in user's private characters`);
            }
            return character;
        } catch (error) {
            console.warn('Failed to fetch private character details:', characterId, error);
            return null;
        }
    }

    async fetchScenarios(language = 'en') {
        const url = catalogJsonUrl(`data/scenarios/scenarios-${language}.json`);
        const data = await this.fetchJSON(url);
        if (Array.isArray(data)) return filterScenariosForSfw(data);
        if (Array.isArray(data?.scenarios)) {
            return { ...data, scenarios: filterScenariosForSfw(data.scenarios) };
        }
        return data;
    }

    invalidate(url) {
        const normalized = this._normalizeUrl(url);
        for (const key of this.cache.keys()) {
            if (!key.includes(normalized)) continue;
            this.cache.delete(key);
            this.cacheExpiry.delete(key);
        }
    }

    clear() {
        this.cache.clear();
        this.cacheExpiry.clear();
        this.pendingRequests.clear();
        console.log('FetchCache: Cache cleared');
    }

    clearExpired() {
        const now = Date.now();
        for (const [key, expiry] of this.cacheExpiry.entries()) {
            if (now >= expiry) {
                this.cache.delete(key);
                this.cacheExpiry.delete(key);
            }
        }
        console.log('FetchCache: Expired entries cleared');
    }

    getStats() {
        const now = Date.now();
        const expiredEntries = Array.from(this.cacheExpiry.entries())
            .filter(([_, expiry]) => now >= expiry).length;

        return {
            cacheSize: this.cache.size,
            pendingRequests: this.pendingRequests.size,
            expiredEntries,
            cacheEntries: Array.from(this.cache.keys()).map(key => ({
                key: key.substring(0, 100) + (key.length > 100 ? '...' : ''),
                isExpired: !this._isValid(key)
            }))
        };
    }

    logCacheStats() {
        const stats = this.getStats();
        console.group('FetchCache Statistics');
        console.log('Cache size:', stats.cacheSize);
        console.log('Pending requests:', stats.pendingRequests);
        console.log('Expired entries:', stats.expiredEntries);
        console.log('Cache entries:', stats.cacheEntries);
        console.groupEnd();
    }

    async preloadCommonResources() {
        try {
            console.log('FetchCache: Preloading common resources...');

            const promises = [
                this.fetchCharacterRankings(),
                this.fetchOutfits('en'),
                this.fetchScenarios('en')
            ];

            await Promise.allSettled(promises);
            console.log('FetchCache: Common resources preloaded');
        } catch (error) {
            console.warn('FetchCache: Error preloading resources:', error);
        }
    }
}

const GLOBAL_FETCH_CACHE_KEY = '__gachatFetchCacheInstance__';
const GLOBAL_FETCH_CACHE_SWEEP_KEY = '__gachatFetchCacheSweepIntervalId__';

const globalScope = typeof globalThis !== 'undefined'
    ? globalThis
    : window;

const isFetchCacheLike = (candidate) => (
    !!candidate
    && typeof candidate.fetch === 'function'
    && typeof candidate.fetchJSON === 'function'
    && typeof candidate.clearExpired === 'function'
);

const existingFetchCache = globalScope[GLOBAL_FETCH_CACHE_KEY];
const fetchCache = isFetchCacheLike(existingFetchCache)
    ? existingFetchCache
    : new FetchCache();

if (!isFetchCacheLike(existingFetchCache)) {
    globalScope[GLOBAL_FETCH_CACHE_KEY] = fetchCache;
}

// Clear expired entries every minute (register only once globally)
if (!globalScope[GLOBAL_FETCH_CACHE_SWEEP_KEY]) {
    globalScope[GLOBAL_FETCH_CACHE_SWEEP_KEY] = setInterval(() => {
        fetchCache.clearExpired();
    }, 60 * 1000);
}

export { fetchCache, FetchCache };
export default fetchCache;
